import {
  AppViewProperty,
  AppViewPropertyKey,
  AppViewPropertyValue,
  DefaultAppViewPropertyId,
  DismissableAppViewProperty,
  GroupTeachingBubbleAppViewProperty,
  NetworkQuestionsOnboardingTipAppViewProperty,
  NetworkQuestionsOnboardingTipStatus,
  SubscriptionTeachingBubbleAppViewProperty,
  SubscriptionTipAppViewProperty,
  defaultAppViewProperties,
} from '../../state/appViewProperties/types';
import { EntityStatus, RequestStatusType, YammerState } from '../../state/types';
import { getStatus } from '../requestStatuses/selectors';

import { getAppViewPropertyRequestStatusId } from './getAppViewPropertyRequestStatusId';

type IsOfAppViewPropertyType = <T extends AppViewPropertyValue>(
  value: AppViewPropertyValue,
  propertyToCheckFor: string[]
) => value is T;
export const isOfAppViewPropertyType: IsOfAppViewPropertyType = <T extends AppViewPropertyValue>(
  value: AppViewPropertyValue,
  propertyToCheckFor: string[]
): value is T => !!value && propertyToCheckFor.every((property) => property in value);

export const isAppViewPropertyFulfilled = (
  state: YammerState,
  entityId: AppViewProperty['id'],
  key: AppViewPropertyKey
) => getAppViewPropertiesStatus(state, entityId, [key]) === 'fulfilled';

type GetAppViewPropertiesStatus = (
  state: YammerState,
  entityId: AppViewProperty['id'],
  propertyKeys: AppViewPropertyKey[]
) => EntityStatus;
export const getAppViewPropertiesStatus: GetAppViewPropertiesStatus = (state, entityId, propertyKeys) => {
  const isFulfilledStatus = propertyKeys.every((key) => getAppViewProperty(state, entityId, key) !== undefined);

  return isFulfilledStatus
    ? 'fulfilled'
    : getStatus(
        state,
        RequestStatusType.AppViewPropertiesRequest,
        getAppViewPropertyRequestStatusId(entityId, propertyKeys)
      );
};

type GetAppViewProperty = (
  state: YammerState,
  entityId: AppViewProperty['id'],
  key: AppViewPropertyKey
) => AppViewPropertyValue;
export const getAppViewProperty: GetAppViewProperty = (
  state: YammerState,
  entityId: AppViewProperty['id'],
  key: AppViewPropertyKey
) => {
  const appViewPropertiesMap = state.appViewProperties[key];

  if (appViewPropertiesMap && appViewPropertiesMap[entityId]) {
    return appViewPropertiesMap[entityId].value;
  }

  return undefined;
};

type GetSupscriptionTipAppViewProperty = (
  state: YammerState,
  entityId: AppViewProperty['id']
) => SubscriptionTipAppViewProperty;
export const getSupscriptionTipAppViewProperty: GetSupscriptionTipAppViewProperty = (
  state: YammerState,
  entityId: AppViewProperty['id']
) => {
  const key = AppViewPropertyKey.CMSubscriptionTip;
  const isPropertyFulfilled = isAppViewPropertyFulfilled(state, entityId, key);
  const appViewPropertiesValue = getAppViewProperty(state, entityId, key);
  const defaultValue = defaultAppViewProperties[key];

  if (
    isPropertyFulfilled &&
    !isOfAppViewPropertyType<SubscriptionTipAppViewProperty>(appViewPropertiesValue, [
      'isDismissed',
      'remindMeLater',
      'remindMeLaterClickedDate',
    ])
  ) {
    return defaultValue as SubscriptionTipAppViewProperty;
  }

  return appViewPropertiesValue as SubscriptionTipAppViewProperty;
};

type GetSupscriptionTeachingBubbleAppViewProperty = (
  state: YammerState,
  entityId: AppViewProperty['id']
) => SubscriptionTeachingBubbleAppViewProperty;
export const getSupscriptionTeachingBubbleAppViewProperty: GetSupscriptionTeachingBubbleAppViewProperty = (
  state: YammerState,
  entityId: AppViewProperty['id']
) => {
  const key = AppViewPropertyKey.CMSubscriptionTeachingBubble;
  const isPropertyFulfilled = isAppViewPropertyFulfilled(state, entityId, key);
  const appViewPropertiesValue = getAppViewProperty(state, entityId, key);
  const defaultValue = defaultAppViewProperties[key];

  if (
    isPropertyFulfilled &&
    !isOfAppViewPropertyType<SubscriptionTeachingBubbleAppViewProperty>(appViewPropertiesValue, ['isDismissed'])
  ) {
    return defaultValue as SubscriptionTeachingBubbleAppViewProperty;
  }

  return appViewPropertiesValue as SubscriptionTeachingBubbleAppViewProperty;
};

type GetGroupTeachingBubbleAppViewProperty = (
  state: YammerState,
  entityId: AppViewProperty['id'],
  key: AppViewPropertyKey
) => GroupTeachingBubbleAppViewProperty;
export const getGroupTeachingBubbleAppViewProperty: GetGroupTeachingBubbleAppViewProperty = (
  state: YammerState,
  entityId: AppViewProperty['id'],
  key: AppViewPropertyKey
) => {
  const appViewPropertiesValue = getAppViewProperty(state, entityId, key);
  const defaultValue = defaultAppViewProperties[key];

  if (!isOfAppViewPropertyType<GroupTeachingBubbleAppViewProperty>(appViewPropertiesValue, ['isDismissed', 'date'])) {
    return defaultValue as GroupTeachingBubbleAppViewProperty;
  }

  return appViewPropertiesValue as GroupTeachingBubbleAppViewProperty;
};

const dismissedOnboardingTipAppViewProperty = {
  status: NetworkQuestionsOnboardingTipStatus.Dismissed,
  shouldShowUpvoteBubble: false,
};

type GetNetworkQuestionsOnboardingTipAppViewProperty = (
  state: YammerState
) => NetworkQuestionsOnboardingTipAppViewProperty;
export const getNetworkQuestionsOnboardingTipAppViewProperty: GetNetworkQuestionsOnboardingTipAppViewProperty = (
  state: YammerState
) => {
  const key = AppViewPropertyKey.NetworkQuestionsOnboardingTip;
  const isPropertyFulfilled = isAppViewPropertyFulfilled(state, DefaultAppViewPropertyId, key);
  const appViewPropertiesValue = getAppViewProperty(state, DefaultAppViewPropertyId, key);
  const defaultValue = defaultAppViewProperties[key];

  if (!isPropertyFulfilled) {
    return dismissedOnboardingTipAppViewProperty;
  }

  if (
    !isOfAppViewPropertyType<NetworkQuestionsOnboardingTipAppViewProperty>(appViewPropertiesValue, [
      'status',
      'shouldShowUpvoteBubble',
    ])
  ) {
    return defaultValue as NetworkQuestionsOnboardingTipAppViewProperty;
  }

  return appViewPropertiesValue as NetworkQuestionsOnboardingTipAppViewProperty;
};

type GetDismissableAppViewProperty = <T extends DismissableAppViewProperty>(
  state: YammerState,
  appViewPropertyKey: AppViewPropertyKey,
  entityId: AppViewProperty['id']
) => T;
export const getDismissableAppViewProperty: GetDismissableAppViewProperty = <T extends DismissableAppViewProperty>(
  state: YammerState,
  appViewPropertyKey: AppViewPropertyKey,
  entityId: AppViewProperty['id']
) => {
  const isPropertyFulfilled = isAppViewPropertyFulfilled(state, entityId, appViewPropertyKey);
  const appViewPropertiesValue = getAppViewProperty(state, entityId, appViewPropertyKey);
  const defaultValue = defaultAppViewProperties[appViewPropertyKey];
  if (!isPropertyFulfilled) {
    const showAsDismissedWhenNotFulfilled = { isDismissed: true };

    return showAsDismissedWhenNotFulfilled as T;
  }

  if (!isOfAppViewPropertyType<DismissableAppViewProperty>(appViewPropertiesValue, ['isDismissed'])) {
    return defaultValue as T;
  }

  return appViewPropertiesValue as T;
};
